import React, { useState, useEffect } from 'react';
import styles from '../CanvassInfo.module.css';
import canvassStyles from '../Canvass.module.css';
import { Button } from '../../layout/buttons';
import { Modal } from '../../layout/Modal';
import { logger } from '../../../utils/logger';
import PreambleQuestions from '../PreambleQuestions';

const experienceOptions = [
  { key: 'BotsInterview', label: 'Bots Interview', description: 'The animated Beep / Boop / Bop bot interview.' },
  { key: 'SignatureChat', label: 'Signature Chat', description: 'The signature-style chat interview.' },
  { key: 'GameAssistantChat', label: 'Assistant Chat', description: 'The original assistant chat interview.' },
];

/**
 * PreviewIntroductions
 * Renders the Interview introduction section and provides a modal to edit interview_introductions.
 *
 * Props:
 * - extractedInsights: object containing interview_introductions and interviewLink
 * - onLocalUpdateIntroductions: (updated: Array) => void  // update parent-local preview state
 * - onSaveIntroductions: (updated: Array) => Promise<void> // persist via parent
 */
export default function PreviewIntroductions({
  extractedInsights,
  onLocalUpdateIntroductions,
  onSaveIntroductions,
  onSaveCanvassPatch,
  canvassConstraints,
  strategyOutline,
  preambleQuestions: preambleQuestionsProp,
  interviewExperience,
  closingMessage: closingMessageProp,
  isEditable = true,
}) {
  const [showIntroModal, setShowIntroModal] = useState(false);
  const [modalIntroductions, setModalIntroductions] = useState([]);
  const [newIntroText, setNewIntroText] = useState('');
  const [isSavingCanvass, setIsSavingCanvass] = useState(false);
  const [closingMessage, setClosingMessage] = useState(closingMessageProp || '');

  const toneOptions = [
    {
      key: 'Casual',
      prompt:
        "Use a friendly, casual tone. Sound warm, approachable, and human—like a helpful peer. Use simple everyday language, short sentences, and light encouragement while staying focused on the interview goals.",
    },
    {
      key: 'Conversational',
      prompt:
        'Use a conversational, neutral-professional tone. Be clear and engaging, ask one question at a time, and mirror the participant\'s language when appropriate. Keep the interview flowing naturally while remaining structured and respectful.',
    },
    {
      key: 'Professional',
      prompt:
        'Use a professional, research-interviewer tone. Be concise, objective, and precise. Avoid slang, keep questions structured, and maintain a calm, respectful demeanor while guiding the participant through the interview.',
    },
  ];

  const initialToneIndex = (() => {
    const existingTone = String(canvassConstraints?.tone || '').trim();
    if (!existingTone) return 1;
    const idx = toneOptions.findIndex((t) => t.prompt === existingTone);
    return idx >= 0 ? idx : 1;
  })();

  const [toneIndex, setToneIndex] = useState(initialToneIndex);

  const depthOptions = [
    {
      key: 'Light',
      description: 'Brief interview (3-4 questions)',
      depth: 1,
    },
    {
      key: 'Balanced',
      description: 'Standard interview (4-6 questions)',
      depth: 2,
    },
    {
      key: 'Deep',
      description: 'Detailed interview (6-8 questions)',
      depth: 3,
    },
  ];

  const initialDepthIndex = (() => {
    const existingDepth = Number(canvassConstraints?.depth || 2);
    const idx = depthOptions.findIndex((d) => d.depth === existingDepth);
    return idx >= 0 ? idx : 1; // Default to Balanced (index 1)
  })();

  const [depthIndex, setDepthIndex] = useState(initialDepthIndex);

  const initialExperienceIndex = (() => {
    const existing = String(interviewExperience || '').trim();
    if (!existing) return 0;
    const idx = experienceOptions.findIndex((e) => e.key === existing);
    return idx >= 0 ? idx : 0;
  })();

  const [experienceIndex, setExperienceIndex] = useState(initialExperienceIndex);

  useEffect(() => {
    const existing = String(interviewExperience || '').trim();
    const idx = experienceOptions.findIndex((e) => e.key === existing);
    setExperienceIndex(idx >= 0 ? idx : 0);
  }, [interviewExperience]);

  useEffect(() => {
    setClosingMessage(closingMessageProp || '');
  }, [closingMessageProp]);

  const saveTone = async (nextIndex) => {
    if (!isEditable || isSavingCanvass) return;
    const selected = toneOptions[nextIndex] || toneOptions[1];

    try {
      setIsSavingCanvass(true);
      if (typeof onSaveCanvassPatch === 'function') {
        await onSaveCanvassPatch({
          constraints: {
            ...(canvassConstraints || {}),
            tone: selected.prompt,
          },
        });
      }
    } catch (err) {
      logger.error('Failed to save tone:', err);
    } finally {
      setIsSavingCanvass(false);
    }
  };

  const saveDepth = async (nextIndex) => {
    if (!isEditable || isSavingCanvass) return;
    const selected = depthOptions[nextIndex] || depthOptions[1];

    try {
      setIsSavingCanvass(true);
      if (typeof onSaveCanvassPatch === 'function') {
        await onSaveCanvassPatch({
          constraints: {
            ...(canvassConstraints || {}),
            depth: selected.depth,
          },
        });
      }
    } catch (err) {
      logger.error('Failed to save depth:', err);
    } finally {
      setIsSavingCanvass(false);
    }
  };

  const saveClosingMessage = async () => {
    if (!isEditable || isSavingCanvass) return;

    try {
      setIsSavingCanvass(true);
      if (typeof onSaveCanvassPatch === 'function') {
        await onSaveCanvassPatch({
          closingMessage: closingMessage.trim(),
        });
      }
    } catch (err) {
      logger.error('Failed to save closing message:', err);
    } finally {
      setIsSavingCanvass(false);
    }
  };

  const saveExperience = async (nextIndex) => {
    if (!isEditable || isSavingCanvass) return;
    const selected = experienceOptions[nextIndex] || experienceOptions[0];

    try {
      setIsSavingCanvass(true);
      if (typeof onSaveCanvassPatch === 'function') {
        await onSaveCanvassPatch({
          interviewExperience: selected.key,
        });
      }
    } catch (err) {
      logger.error('Failed to save interview experience:', err);
    } finally {
      setIsSavingCanvass(false);
    }
  };

  const openModal = (e) => {
    e?.preventDefault?.();
    if (!isEditable) return;
    const intros = Array.isArray(extractedInsights?.interview_introductions)
      ? extractedInsights.interview_introductions.map((it) => ({ ...it }))
      : [];
    setModalIntroductions(intros);
    const selectedCustom = intros.find(
      (it) => it.selected === true && it.type === 'custom'
    );
    setNewIntroText(selectedCustom?.introduction_text || '');
    setShowIntroModal(true);
  };

  const save = async (updatedOverride = null, newIntroTextOverride = null) => {
    try {
      setIsSavingCanvass(true);
      const hasArrayOverride = Array.isArray(updatedOverride);
      let updated = hasArrayOverride ? [...updatedOverride] : [...modalIntroductions];
      const typedSource = hasArrayOverride ? newIntroTextOverride : null;
      const typed = ((typedSource ?? newIntroText) || '').trim();

      // Ensure at most one custom intro
      const customIndices = updated
        .map((it, i) => (it && it.type === 'custom' ? i : -1))
        .filter((i) => i >= 0);

      if (typed.length > 0) {
        updated = updated.map((it) => ({ ...it, selected: false }));
        if (customIndices.length > 0) {
          const keepIndex = customIndices[0];
          updated[keepIndex] = {
            ...updated[keepIndex],
            type: 'custom',
            introduction_text: typed,
            badge_theme: updated[keepIndex]?.badge_theme ?? '',
            sample_badges: Array.isArray(updated[keepIndex]?.sample_badges)
              ? updated[keepIndex].sample_badges
              : [],
            selected: true,
          };
          if (customIndices.length > 1) {
            updated = updated.filter(
              (_, idx) => idx === keepIndex || !customIndices.slice(1).includes(idx)
            );
          }
        } else {
          updated.push({
            type: 'custom',
            introduction_text: typed,
            badge_theme: updated[0]?.badge_theme ?? '',
            sample_badges: updated[0]?.sample_badges ?? [],
            selected: true,
          });
        }
      } else {
        if (customIndices.length > 1) {
          const keepIndex = customIndices[0];
          updated = updated.filter(
            (_, idx) => idx === keepIndex || !customIndices.slice(1).includes(idx)
          );
        }
      }

      // Local preview update
      onLocalUpdateIntroductions?.(updated);

      // Persist via parent
      if (typeof onSaveIntroductions === 'function') {
        await onSaveIntroductions(updated);
      }
      setShowIntroModal(false);
    } catch (err) {
      logger.error('Failed to save canvass:', err);
    } finally {
      setIsSavingCanvass(false);
    }
  };

  return (
    <>
      <section className={styles.section + ' ' + styles.sectionFirst}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitle}>Interview Tone Control</div>
        </div>
        <div className={styles.inputSubLabel}>
          Choose the tone the interviewer should use during the interview
        </div>

        <div className={styles.toneControlWrap}>
          <input
            type="range"
            min={0}
            max={2}
            step={1}
            value={toneIndex}
            disabled={!isEditable || isSavingCanvass}
            onChange={(e) => {
              const next = Number(e.target.value);
              setToneIndex(next);
            }}
            onMouseUp={() => saveTone(toneIndex)}
            onTouchEnd={() => saveTone(toneIndex)}
            className={styles.toneSlider}
            style={{ '--tone-pct': `${(toneIndex / 2) * 100}%` }}
          />

          <div className={styles.toneLabels}>
            {toneOptions.map((opt, idx) => {
              const isSelected = idx === toneIndex;
              return (
                <div
                  key={opt.key}
                  onClick={() => {
                    if (!isEditable || isSavingCanvass) return;
                    setToneIndex(idx);
                    saveTone(idx);
                  }}
                  className={`${styles.toneLabel} ${isSelected ? styles.toneLabelSelected : ''}`}
                  style={{
                    cursor: isEditable ? 'pointer' : 'not-allowed',
                    opacity: isEditable ? 1 : 0.6,
                  }}
                  title={opt.prompt}
                >
                  {opt.key}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitle}>Interview Depth Control</div>
        </div>
        <div className={styles.inputSubLabel}>
          Choose how deeply the canvass should probe
        </div>

        <div className={styles.toneControlWrap}>
          <input
            type="range"
            min={0}
            max={2}
            step={1}
            value={depthIndex}
            disabled={!isEditable || isSavingCanvass}
            onChange={(e) => {
              const next = Number(e.target.value);
              setDepthIndex(next);
            }}
            onMouseUp={() => saveDepth(depthIndex)}
            onTouchEnd={() => saveDepth(depthIndex)}
            className={styles.toneSlider}
            style={{ '--tone-pct': `${(depthIndex / 2) * 100}%` }}
          />

          <div className={styles.toneLabels}>
            {depthOptions.map((opt, idx) => {
              const isSelected = idx === depthIndex;
              return (
                <div
                  key={opt.key}
                  onClick={() => {
                    if (!isEditable || isSavingCanvass) return;
                    setDepthIndex(idx);
                    saveDepth(idx);
                  }}
                  className={`${styles.toneLabel} ${isSelected ? styles.toneLabelSelected : ''}`}
                  style={{
                    cursor: isEditable ? 'pointer' : 'not-allowed',
                    opacity: isEditable ? 1 : 0.6,
                  }}
                  title={opt.description}
                >
                  <div>{opt.key}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitle}>Interview Experience</div>
        </div>
        <div className={styles.inputSubLabel}>
          Choose which interview experience participants see
        </div>

        <div className={styles.pills}>
          {experienceOptions.map((opt, idx) => {
            const isSelected = idx === experienceIndex;
            return (
              <div
                key={opt.key}
                onClick={() => {
                  if (!isEditable || isSavingCanvass) return;
                  setExperienceIndex(idx);
                  saveExperience(idx);
                }}
                className={styles.pill}
                style={{
                  cursor: isEditable ? 'pointer' : 'not-allowed',
                  opacity: isEditable ? 1 : 0.6,
                  border: isSelected ? '2px solid #0d6efd' : undefined,
                  boxShadow: isSelected ? '0 0 0 2px rgba(13,110,253,0.15) inset' : undefined,
                }}
                title={opt.description}
              >
                {opt.label}
              </div>
            );
          })}
        </div>
      </section> */}

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitle}>Closing Message</div>
        </div>
        <div className={styles.inputSubLabel}>
          Enter a message to convey to the end user during the interview
        </div>
        <textarea
          value={closingMessage}
          onChange={(e) => setClosingMessage(e.target.value)}
          disabled={!isEditable || isSavingCanvass}
          rows={4}
          style={{
            width: '100%',
            padding: '12px',
            border: '1px solid var(--brand-as-neutral-black-transparent-10, rgba(12,22,34,0.1))',
            borderRadius: '8px',
            resize: 'vertical',
            fontFamily: 'inherit',
            fontSize: '14px',
            lineHeight: '20px',
          }}
          placeholder="Add a closing message or important note for the interview participant..."
        />
        <div className={styles.buttonEnd}>
          <Button
            text="Save Closing Message"
            onClick={saveClosingMessage}
            disabled={!isEditable || isSavingCanvass}
          >
            Save Closing Message
          </Button>
        </div>
      </section>

      <PreambleQuestions
        preambleQuestions={extractedInsights?.preamble_questions || preambleQuestionsProp || []}
        onUpdate={(updatedQuestions) => {
          // Update local state immediately for preview
          if (typeof onLocalUpdateIntroductions === 'function') {
            const updatedInsights = {
              ...extractedInsights,
              preamble_questions: updatedQuestions
            };
            onLocalUpdateIntroductions(updatedInsights.preamble_questions);
          }
        }}
        onSave={async (updatedQuestions) => {
          try {
            setIsSavingCanvass(true);
            if (typeof onSaveCanvassPatch === 'function') {
              await onSaveCanvassPatch({
                preamble_questions: updatedQuestions
              });
            }
          } catch (err) {
            logger.error('Failed to save preamble questions:', err);
            throw err;
          } finally {
            setIsSavingCanvass(false);
          }
        }}
        isEditable={isEditable}
      />

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitle}>Interview introduction</div>
          {isEditable && (
            <a className={styles.editLink} type="button" onClick={openModal}>
              Edit
            </a>
          )}
        </div>
        <div className={styles.inputSubLabel}>Select your preferred interview introduction</div>
        <div className={styles.pills + ' ' + styles.sectionLast}>
          {(extractedInsights?.interview_introductions || []).map((intro, idx) => (
            <div
              key={idx}
              className={`${styles.pillIntro} ${canvassStyles.previewPill}`}
              title={intro.introduction_text || ''}
              aria-selected={intro.selected === true}
              onClick={() => {
                if (!isEditable || isSavingCanvass) return;
                const current = Array.isArray(extractedInsights?.interview_introductions)
                  ? extractedInsights.interview_introductions.map((it) => ({ ...it }))
                  : [];
                const updated = current.map((it, i) => ({ ...it, selected: i === idx }));
                const selectedIntro = current[idx];
                const typed = selectedIntro && selectedIntro.type === 'custom'
                  ? selectedIntro.introduction_text || ''
                  : '';
                save(updated, typed);
              }}
              style={{
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
                cursor: isEditable ? 'pointer' : 'not-allowed',
                opacity: isEditable ? 1 : 0.6,
                ...(intro.selected === true
                  ? {
                      border: '2px solid #0d6efd',
                      boxShadow: '0 0 0 2px rgba(13,110,253,0.15) inset',
                    }
                  : {}),
              }}
            >
              {intro.introduction_text || intro.badge_theme || 'Introduction'}
            </div>
          ))}
        </div>
      </section>

      <Modal
        open={showIntroModal}
        onClose={() => setShowIntroModal(false)}
        title="Select or add an introduction"
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, width: '100%' }}>
            <Button variant="outline-neutral" onClick={() => setShowIntroModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" disabled={!isEditable || isSavingCanvass} onClick={() => save()}>
              {isSavingCanvass ? 'Saving…' : 'Save'}
            </Button>
          </div>
        }
      >
        <div>
          <div style={{ marginBottom: 12, color: 'var(--text-secondary, #6c757d)' }}>
            Click to select an existing introduction:
          </div>
          <div style={{ display: 'grid', gap: 8 }}>
            {(modalIntroductions || []).map((intro, idx) => {
              const isSelected = intro.selected === true;
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => {
                    if (!isEditable) return;
                    setModalIntroductions((prev) =>
                      prev.map((it, i) => ({ ...it, selected: i === idx }))
                    );
                    const isCustom = intro && intro.type === 'custom';
                    setNewIntroText(isCustom ? intro.introduction_text || '' : '');
                  }}
                  style={{
                    textAlign: 'left',
                    padding: '12px 14px',
                    borderRadius: 8,
                    border: isSelected ? '2px solid #0d6efd' : '1px solid #e5e7eb',
                    background: '#fff',
                    cursor: isEditable ? 'pointer' : 'not-allowed',
                    opacity: isEditable ? 1 : 0.6,
                  }}
                  title={intro.introduction_text || ''}
                  >
                  <div style={{ fontWeight: 500, marginBottom: 4 }}>
                    {intro.introduction_text || intro.badge_theme || 'Introduction'}
                    {isSelected && (
                      <span style={{ marginLeft: 8, color: '#0d6efd' }}>(selected)</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          <div style={{ marginTop: 20 }}>
            <div style={{ marginBottom: 8, color: 'var(--text-secondary, #6c757d)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Or type a new introduction:</span>
              {isEditable && (() => {
                const selectedIntro = modalIntroductions.find(intro => intro.selected === true);
                return selectedIntro && selectedIntro.type !== 'custom' ? (
                  <button
                    type="button"
                    onClick={() => {
                      setNewIntroText(selectedIntro.introduction_text || '');
                      // Deselect all introductions when copying to textarea
                      setModalIntroductions((prev) =>
                        prev.map((it) => ({ ...it, selected: false }))
                      );
                    }}
                    style={{
                      padding: '4px 8px',
                      fontSize: 12,
                      borderRadius: 4,
                      border: '1px solid #e5e7eb',
                      background: '#f8f9fa',
                      cursor: 'pointer',
                      color: '#6c757d'
                    }}
                    title="Copy selected introduction to edit"
                  >
                    Copy Selected
                  </button>
                ) : null;
              })()}
            </div>
            <textarea
              value={newIntroText}
              onChange={(e) => {
                if (!isEditable) return;
                const val = e.target.value.slice(0, 500);
                setNewIntroText(val);
                if (val && val.trim().length > 0) {
                  setModalIntroductions((prev) =>
                    prev.map((it) => ({ ...it, selected: false }))
                  );
                }
              }}
              placeholder="Write a short introduction text..."
              rows={4}
              readOnly={!isEditable}
              maxLength={500}
              style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e5e7eb', background: !isEditable ? '#f8f9fa' : '#fff' }}
            />
            <div style={{ marginTop: 6, fontSize: 12, color: '#6c757d', textAlign: 'right' }}>
              {newIntroText?.length || 0}/500
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}
